import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Wallet, ExternalLink, Scale, Coins, AlertTriangle, LineChart } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { logEvent } from "@/lib/api";

const CAT_LABEL = {
  descent: "Citizenship by descent", marriage: "Fast-track by marriage",
  naturalization: "Fast-track naturalization", investment: "Citizenship by investment",
  residence: "Residence permit",
};
const DIFF_COLOR = {
  Easy: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Moderate: "bg-amber-50 text-amber-900 border-amber-200",
  Hard: "bg-rose-50 text-rose-800 border-rose-200",
};

const TIER_LABEL = {
  base: { label: "Citizenship", cls: "bg-[hsl(var(--accent))] text-[hsl(var(--atlas-navy))] border-0" },
  full: { label: "Full Mobility", cls: "bg-[hsl(var(--atlas-navy))] text-white border-0" },
};

const rate = (v) => (v === null || v === undefined || v === "" ? "—" : (typeof v === "number" ? `${v}%` : v));
const isUrl = (s) => typeof s === "string" && /^https?:\/\//.test(s);
const domain = (u) => { try { return new URL(u).hostname.replace("www.", ""); } catch (e) { return "source"; } };

const TaxCell = ({ label, value }) => (
  <div className="rounded-lg border bg-[hsl(var(--background))] px-2.5 py-2">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">{value}</div>
  </div>
);

export const PathwayCard = ({ p, index }) => {
  const tax = p.tax_summary || {};
  const tierMeta = TIER_LABEL[p.tier];
  const cbi = p.cbi_details;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3) }}>
      <Card className="p-5 bg-white card-hover" data-testid="pathway-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-semibold text-lg text-[hsl(var(--atlas-navy))]">{p.country}</span>
              <Badge variant="secondary" className="text-[11px]">{CAT_LABEL[p.category] || p.category}</Badge>
              {p.is_personalized && <Badge className="text-[11px] bg-[hsl(var(--accent))] text-[hsl(var(--atlas-navy))] border-0">Matched to you</Badge>}
              {tierMeta && (
                <Badge data-testid="pathway-tier-badge" className={`text-[11px] ${tierMeta.cls}`}>{tierMeta.label}</Badge>
              )}
            </div>
            <div className="text-sm text-[hsl(var(--atlas-slate))] mt-0.5">{p.pathway_name}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums" data-testid="pathway-accessibility-score">{p.score}</div>
            <div className="text-[10px] text-muted-foreground">accessibility</div>
          </div>
        </div>

        <div className="mt-3 w-full"><Progress value={p.score} className="h-1.5" /></div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Badge variant="outline" className={DIFF_COLOR[p.difficulty] || ""}>{p.difficulty}</Badge>
          <span className="px-2 py-1 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--atlas-slate))] inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.timeline}</span>
          <span className="px-2 py-1 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--atlas-slate))] inline-flex items-center gap-1"><Wallet className="h-3 w-3" /> {p.cost_range}</span>
          <span className="px-2 py-1 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--atlas-slate))]">Dual: {p.dual_citizenship}</span>
        </div>

        {p.why_matched && <p className="mt-3 text-sm text-[hsl(var(--atlas-slate))]">{p.why_matched}</p>}

        {p.counsel_note && (
          <div
            data-testid="pathway-counsel-note"
            className="mt-3 flex items-start gap-2 rounded-lg border border-[hsl(var(--atlas-steel))]/25 bg-[hsl(var(--muted))]/60 px-3 py-2"
          >
            <Scale className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--atlas-steel))]" />
            <p className="text-xs text-[hsl(var(--atlas-slate))]">
              <span className="font-medium text-[hsl(var(--atlas-navy))]">Verify with counsel:</span> {p.counsel_note}
            </p>
          </div>
        )}

        {p.data_confidence?.inbound_naturalization === "low" && (
          <div
            data-testid="pathway-data-confidence"
            className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50/70 px-3 py-2"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" />
            <p className="text-xs text-amber-900">
              <span className="font-medium">Law on paper vs practice:</span> Processing capacity in {p.country} is currently limited or disrupted{p.data_confidence.reason ? ` (${p.data_confidence.reason})` : ""}, so actual timelines may differ substantially from the statutory figure.
              {p.structural_exclusion && (
                <span className="block mt-1">
                  <span className="font-medium">Structural exclusion:</span> Myanmar&apos;s 1982 Citizenship Law is ethnicity-based; naturalization for those outside recognized &quot;national races&quot; is effectively closed independent of political administration.
                </span>
              )}
            </p>
          </div>
        )}

        <Accordion type="single" collapsible className="mt-2">
          <AccordionItem value="details" className="border-0">
            <AccordionTrigger className="py-2 text-sm text-[hsl(var(--atlas-navy))] hover:no-underline">Requirements &amp; tax summary</AccordionTrigger>
            <AccordionContent>
              {p.requirements?.length > 0 && (
                <ul className="space-y-1 text-sm text-[hsl(var(--atlas-slate))] mb-4">
                  {p.requirements.map((r, i) => (<li key={i} className="flex gap-2"><span className="text-[hsl(var(--atlas-teal))]">•</span> {r}</li>))}
                </ul>
              )}
              <div className="grid grid-cols-3 gap-2" data-testid="pathway-tax-summary">
                <TaxCell label="Income" value={rate(tax.income_tax_top_rate_pct)} />
                <TaxCell label="Corporate" value={rate(tax.corporate_tax_rate_pct)} />
                <TaxCell label="VAT/GST" value={rate(tax.vat_gst_standard_rate_pct)} />
                <TaxCell label="Social sec." value={rate(tax.social_security_employee_pct)} />
                <TaxCell label="Wealth tax" value={tax.wealth_tax || "—"} />
                <TaxCell label="System" value={tax.tax_system || "—"} />
              </div>
              {tax.special_individual_tax_schemes && !/not researched/i.test(tax.special_individual_tax_schemes) && (
                <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-[hsl(var(--atlas-navy))]">Special regime:</span> {tax.special_individual_tax_schemes}</p>
              )}
              {tax.data_confidence && tax.data_confidence !== "high" && (
                <div
                  data-testid="pathway-tax-confidence"
                  className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 ${
                    tax.data_confidence === "low"
                      ? "border-rose-300/60 bg-rose-50/70"
                      : "border-amber-300/60 bg-amber-50/70"
                  }`}
                >
                  <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
                    tax.data_confidence === "low" ? "text-rose-700" : "text-amber-700"
                  }`} />
                  <p className={`text-xs ${tax.data_confidence === "low" ? "text-rose-900" : "text-amber-900"}`}>
                    <span className="font-medium">Reduced-confidence tax data:</span>{" "}
                    Figures for {p.country} carry <span className="font-medium">{tax.data_confidence}</span> confidence
                    {tax.last_verified ? ` (last verified ${tax.last_verified})` : ""}
                    {tax.source_url ? ". " : "."}
                    {tax.source_url && (
                      <a
                        href={tax.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-current/40 hover:decoration-current"
                        data-testid="pathway-tax-source-link"
                      >
                        verify with primary source
                      </a>
                    )}
                    {tax.source_url ? "." : ""} Spot-check any specific figure before relying on it.
                  </p>
                </div>
              )}
              {/* Reverse link: bridge from eligibility report into the Tax Explorer,
                  pre-highlighted on this country. */}
              <Link
                to={`/tax?highlight=${encodeURIComponent(p.country)}`}
                data-testid="pathway-tax-explorer-link"
                onClick={() => logEvent("pathway_to_tax_clicked", null, { country: p.country, pathway_category: p.category })}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--atlas-navy))] hover:text-[hsl(var(--atlas-teal))] underline decoration-[hsl(var(--atlas-teal))]/40 underline-offset-4 hover:decoration-[hsl(var(--atlas-teal))]"
              >
                <LineChart className="h-3 w-3" />
                Compare {p.country}&apos;s tax rates against other countries
              </Link>
            </AccordionContent>
          </AccordionItem>

          {cbi && (
            <AccordionItem value="cbi" className="border-0 border-t border-[hsl(var(--border))]/60">
              <AccordionTrigger
                data-testid="pathway-cbi-trigger"
                className="py-2 text-sm text-[hsl(var(--atlas-navy))] hover:no-underline"
              >
                <span className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-[hsl(var(--atlas-teal))]" /> Investment (CBI): neutral details
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 text-xs text-[hsl(var(--atlas-slate))]">
                  {cbi.all_in_cost && (
                    <p><span className="font-medium text-[hsl(var(--atlas-navy))]">All-in cost:</span> {cbi.all_in_cost}</p>
                  )}
                  {cbi.investment_recoverability && (
                    <p><span className="font-medium text-[hsl(var(--atlas-navy))]">Recoverability:</span> {cbi.investment_recoverability}</p>
                  )}
                  {cbi.program_risks && (
                    <p><span className="font-medium text-[hsl(var(--atlas-navy))]">Program risks:</span> {cbi.program_risks}</p>
                  )}
                  {cbi.zero_commission && (
                    <div className="mt-2 rounded-lg border border-[hsl(var(--success))]/25 bg-[hsl(var(--success))]/5 px-2.5 py-2">
                      <p className="text-[11px] text-[hsl(var(--atlas-slate))]">
                        <span className="font-medium text-[hsl(var(--atlas-navy))]">Conflict-free:</span> {cbi.zero_commission}
                      </p>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        <div className="mt-2 pt-3 border-t flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {p.verification_pending ? (
              <Badge className="bg-amber-50 text-amber-900 border border-amber-200"><Clock className="h-3 w-3 mr-1" /> Verification pending</Badge>
            ) : (
              <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</Badge>
            )}
            {p.last_verified && <span className="text-[11px] text-muted-foreground">Last verified: {p.last_verified}</span>}
          </div>
          {p.source_url && (isUrl(p.source_url) ? (
            <a href={p.source_url} target="_blank" rel="noreferrer" data-testid="pathway-source-link"
              className="text-xs inline-flex items-center gap-1 underline underline-offset-4 decoration-[hsl(var(--atlas-teal))]/40 hover:decoration-[hsl(var(--atlas-teal))] text-[hsl(var(--atlas-navy))]">
              {domain(p.source_url)} <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-[11px] text-muted-foreground max-w-[50%] truncate" title={p.source_url}>Source: {p.source_url}</span>
          ))}
        </div>
      </Card>
    </motion.div>
  );
};
